import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { useToastStore } from "../../stores/toastStore";
import kearneyLogo from "../../assets/images/kearney-logo-black.svg";
import pipe from "../../assets/images/pipe.svg";
import ssoVisual from "../../assets/images/Login_photo.png";
import "./Login.scss";
import "../../components/TermsModal/TermsModal.scss";
import { privacyPolicySections } from "../../constants/privacyPolicySections";

const Login = () => {
  const navigate = useNavigate();
  const setToken = useAuthStore((state) => state.setToken);
  const toast = useToastStore();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const baseUrl = import.meta.env.VITE_API_BASE_URL || "";
  const appBaseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
  const tenantId = import.meta.env.VITE_MSAL_TENANT_ID;
  const clientId = import.meta.env.VITE_MSAL_CLIENT_ID;
  const redirectUri =
    import.meta.env.VITE_MSAL_REDIRECT_URI || `${appBaseUrl}/login`;
  const scope =
    import.meta.env.VITE_MSAL_SCOPES ||
    "openid offline_access https://graph.microsoft.com/User.Read";
  const isSsoConfigured = Boolean(tenantId && clientId && redirectUri);

  const azureOauthUri = useMemo(() => {
    if (!isSsoConfigured) return "";
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: "code",
      redirect_uri: redirectUri,
      response_mode: "query",
      scope,
      state: "12345",
    });
    return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
  }, [clientId, isSsoConfigured, redirectUri, scope, tenantId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) return;

    const exchangeCode = async () => {
      setLoading(true);
      try {
        if (!baseUrl) {
          throw new Error("API base URL is not configured");
        }
        const params = new URLSearchParams({
          code,
          redirect_uri: redirectUri,
        });
        const url = `${baseUrl}api/v1/auth/getAToken?${params.toString()}`;
        const resp = await fetch(url, { method: "GET" });
        if (!resp.ok) {
          const detail = await resp.json().catch(() => ({}));
          throw new Error(detail?.detail || "SSO token exchange failed");
        }
        const data = await resp.json();
        const token = data.access_token;
        if (!token) {
          throw new Error("SSO login failed");
        }
        localStorage.setItem("token", JSON.stringify(token));
        setToken(token);
        await fetch(`${baseUrl}api/v1/identity`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate("/");
      } catch (err: any) {
        toast.error("SSO login failed", err?.message || "Please try again.");
      } finally {
        setLoading(false);
      }
    };

    exchangeCode();
  }, [baseUrl, navigate, redirectUri, setToken]);

  const handleLogin = async () => {
    try {
      if (!isSsoConfigured || !azureOauthUri) {
        throw new Error("SSO is not configured");
      }
      if (!termsChecked) {
        throw new Error("Please accept the Terms and Conditions");
      }
      window.location.href = azureOauthUri;
    } catch (err: any) {
      toast.error("SSO login failed", err?.message || "Please try again.");
    }
  };

  const handleLocalLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (!termsChecked) {
        throw new Error("Please accept the Terms and Conditions");
      }
      const resp = await fetch(`${baseUrl}api/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });
      if (!resp.ok) {
        let message = "Invalid credentials";
        try {
          const detail = await resp.json();
          if (typeof detail?.detail === "string" && detail.detail.trim()) {
            message = detail.detail;
          } else if (typeof detail?.message === "string" && detail.message.trim()) {
            message = detail.message;
          }
        } catch {
          // ignore parse errors, keep default message
        }
        throw new Error(message);
      }
      const data = await resp.json();
      const token = data.access_token;
      if (!token) {
        throw new Error("Login failed");
      }
      localStorage.setItem("token", JSON.stringify(token));
      setToken(token);
      await fetch(`${baseUrl}api/v1/identity`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      navigate("/");
    } catch (err: any) {
      toast.error("Login failed", err?.message || "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-panel">
        <div className="login-brand">
          <img src={kearneyLogo} alt="Kearney" />
          <img src={pipe} alt="" />
          <span className="login-product">NegotiationIQ</span>
        </div>
        <p className="login-copy">
          NegotiationIQ supports strategic negotiation preparation with AI-driven insights,
          structured analysis, and rapid document understanding.
        </p>
        <div className="login-sso">
          <button
            type="button"
            className="login-button"
            onClick={handleLogin}
            disabled={!termsChecked}
          > 
            {loading ? "Signing in..." : "Login with SSO"}
          </button>
          {/* {!isSsoConfigured ? (
            <div className="login-hint">SSO is not configured yet.</div>
          ) : null} */}
        </div>

        <div className="login-divider">
          <span>or</span>
        </div>

        <form className="login-form" onSubmit={handleLocalLogin}>
          <label className="login-label">
            Username
            <input
              className="login-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </label>
          <label className="login-label">
            Password
            <div className="login-password-field">
              <input
                className="login-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                <i className={showPassword ? "pi pi-eye-slash" : "pi pi-eye"} />
              </button>
            </div>
          </label>
          <button
            type="submit"
            className="login-button login-button-secondary"
            disabled={loading || !termsChecked}
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
          <div className="login-terms">
            <label className="login-terms-checkbox">
              <input
                type="checkbox"
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
              />
              <span className="login-terms-label">
                I accept the{" "}
                <span
                  className="login-terms-link"
                  onClick={() => setShowTermsDialog(true)}
                >
                  Terms and Conditions
                </span>
              </span>
            </label>
          </div>
        </form>
      </div>
      {showTermsDialog && (
        <div className="terms-modal__backdrop">
          <div className="terms-modal__content">
            <div className="terms-modal__header">
              <h2>Terms and Conditions</h2>
              <button
                type="button"
                className="terms-modal__close"
                onClick={() => setShowTermsDialog(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="terms-modal__body">
              {privacyPolicySections.data.map((section, index) => (
                <div key={`${section.title}-${index}`} className="terms-modal__section">
                  <h3>{section.title}</h3>
                  {section.description ? <p>{section.description}</p> : null}
                  {section.list ? (
                    <ul>
                      {section.list.map((item, idx) => (
                        <li key={`${item.title || "item"}-${idx}`}>
                          {item.title ? <strong>{item.title}</strong> : null} {item.content}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="terms-modal__actions">
              <button
                type="button"
                className="login-button"
                onClick={() => setShowTermsDialog(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className="login-visual"
        aria-hidden="true"
        style={{ backgroundImage: `url(${ssoVisual})` }}
      />
      <div className="login-footer">© 2025 Kearney. All rights reserved</div>
    </div>
  );
};

export default Login;
